/**
 * ENG-8575 (slice 3 of ENG-8427): the manager-side arming layer for the
 * tool-call audit scanner.
 *
 * `scanAgentToolCalls` (ENG-8574) is a pure scanner: hand it a salt, a session
 * list and an API, and it scans. It has no throttle, no gate, and no opinion
 * about whether it should be running. This module is the part that decides,
 * and it is deliberately separate so the manager tick gains one guarded call
 * rather than fifty lines of policy.
 *
 * It owns four things:
 *
 *  1. THE THROTTLE. Every neighbouring periodic task self-throttles; the
 *     scanner does not. The tick is ~10s nominal and ~29s at the fleet median,
 *     and `extractTranscriptWindow` is synchronous, so an unthrottled scan puts
 *     a full-file read on the poll cycle twice a minute. Transcripts persist 7
 *     days locally, so a 10-minute lag costs nothing.
 *
 *  2. THE ENTITLEMENT LATCH. See the long note on `entitlementLatch` below.
 *
 *  3. THE SALT. Resolved per agent and passed through; see tool-call-path-salt.
 *
 *  3b. THE CHARTER'S `logging_mode` (ENG-8726). Resolved per agent from the
 *     provisioned CHARTER.md and passed through as `hashOnly`; see
 *     agent-logging-mode. Resolved HERE and not at the manager call site for the
 *     same reason as the salt: this is the layer with a throttle, so the read
 *     happens once per 10 minutes rather than once per poll.
 *
 *  4. NEVER THROWING. `scanAgentToolCalls` contains its own HTTP faults, but
 *     the synchronous work either side of it (cursor path resolution, session
 *     peek, salt IO) can still throw, and the manager call site is a bare
 *     `void` with no `.catch()` — matching every sibling task. So the whole
 *     body is wrapped here rather than relying on the caller.
 */

import { homedir } from 'node:os';
import { join } from 'node:path';

import { loggingModeWithholdsTargets, readAgentLoggingMode } from './agent-logging-mode.js';
import { peekCurrentSession, sessionTranscriptDir } from './daily-session.js';
import { getProjectDir } from './persistent-session.js';
import { readToolCallPathSalt } from './tool-call-path-salt.js';
import { scanAgentToolCalls, type ScanApi } from './tool-call-scan.js';

/** Matches `MIN_CHECK_INTERVAL_MS` in memory-extractor.ts, the sibling task. */
const MIN_CHECK_INTERVAL_MS = 10 * 60_000;

/**
 * How long a `not_entitled` answer is trusted before the host asks again.
 *
 * Long enough that an un-entitled org costs one request every half hour rather
 * than one per scan; short enough that a customer who buys the add-on starts
 * being collected within the same support call, without a manager restart.
 */
const ENTITLEMENT_RECHECK_MS = 30 * 60_000;

interface AgentAuditState {
  lastCheckedAt: number;
  /**
   * When the API last answered `entitled: false` for this agent, or null if it
   * never has. See `entitlementLatch` below.
   */
  notEntitledAt: number | null;
}

const state = new Map<string, AgentAuditState>();

/** Test seam, mirroring `_resetMemoryExtractorState`. */
export function _resetToolCallAuditState(): void {
  state.clear();
}

/**
 * THE ENTITLEMENT LATCH — why the host does not resolve plans.
 *
 * ENG-8575 specifies a host-side entitlement check that is "an efficiency skip,
 * never the control", and says the host should learn entitlement "through the
 * existing desired-state path (/host/refresh) or flag materialization".
 *
 * There is no such path. `effectiveAdvancedGovernance` is resolved in exactly
 * two places, both inside the tool-call ingest handlers; neither /host/refresh
 * nor /host/heartbeat carries an entitlement field, and the manager's response
 * type has no slot for one. Implementing the AC literally against a signal that
 * never arrives would leave entitlement permanently "unknown" — and if unknown
 * skipped, the scanner would collect nothing, forever, while every gate in the
 * system reported healthy.
 *
 * So the host learns entitlement from the authoritative gate's OWN answer. The
 * route already replies `200 {entitled:false}` on a definitive refusal, and the
 * scanner already classifies that as `not_entitled`. We record when we last saw
 * it and skip scanning until the recheck window elapses.
 *
 * This satisfies "efficiency skip, never the control" by construction, in a way
 * a cached plan value could not:
 *
 *  - The latch can only ever be SET by the API refusing. The host cannot talk
 *    itself into collecting, and it cannot talk itself out of it either — a
 *    latch that has never been set does not skip.
 *  - It cannot disagree with the gate, because it IS the gate's answer. A
 *    second copy of the entitlement in a desired-state payload could go stale
 *    and contradict the route.
 *  - It costs no plan resolution on the heartbeat path, which runs per host per
 *    poll.
 *
 * UNKNOWN ATTEMPTS, IT DOES NOT SKIP — a deliberate reading of AC5. The route
 * fails OPEN on entitlement uncertainty (ENG-8574) precisely because a withheld
 * audit row is unrecoverable: the transcript behind it is gone in 7 days
 * locally, 30 in Claude Code's own cleanup. A host that failed CLOSED on
 * uncertainty would defeat that from the other side — the row would never reach
 * the route that was willing to collect it. Attempting on unknown is therefore
 * the fail-safe direction, and it is not a retry loop: one attempt resolves to
 * either `entitled:true` (proceed) or `entitled:false` (latch, then quiet).
 */
function entitlementLatch(entry: AgentAuditState | undefined, nowMs: number): boolean {
  if (!entry?.notEntitledAt) return false;
  return !hasElapsed(entry.notEntitledAt, nowMs, ENTITLEMENT_RECHECK_MS);
}

/**
 * Has `windowMs` passed since `sinceMs`?
 *
 * A backwards clock — an NTP correction, a VM resuming from a snapshot — makes
 * `now - since` NEGATIVE, and a bare `delta < windowMs` then reads that as "no
 * time has passed" for as long as the skew lasts. On the throttle that stalls
 * scanning; on the latch it extends an un-entitled skip. Both are the quiet,
 * unexplainable wedge AC5 forbids, and neither logs anything.
 *
 * So a negative or non-finite delta counts as ELAPSED: the failure direction is
 * one extra scan, not an indefinite stall. Mirrors the same guard on the
 * unpaired grace check in tool-call-cursor.ts.
 */
function hasElapsed(sinceMs: number, nowMs: number, windowMs: number): boolean {
  const delta = nowMs - sinceMs;
  if (!Number.isFinite(delta) || delta < 0) return true;
  return delta >= windowMs;
}

/**
 * For one agent: scan transcripts for tool calls and ship them. Self-throttled,
 * entitlement-latched, and non-throwing.
 *
 * The caller is responsible for the feature gate — this function assumes it has
 * already been decided that auditing is on. That split is intentional: the
 * flag-off path must do NO work at all (AC3), and the cheapest way to prove
 * that is for the gate to sit outside a module that would otherwise resolve a
 * salt and peek at session state before returning.
 */
export async function maybeScanToolCalls(args: {
  api: ScanApi;
  codeName: string;
  agentId: string;
  log: (msg: string) => void;
  now?: () => number;
  /** Test seam: override the transcript directory. */
  transcriptDir?: string;
  /** Test seam: override `~/.claude/projects`. */
  projectsRoot?: string;
  /** Test seam: override the cursor state file. */
  cursorPath?: string;
  /** Test seam: override `$HOME` for salt + cursor resolution. */
  homeDir?: string;
}): Promise<void> {
  const { api, codeName, agentId, log } = args;
  const nowFn = args.now ?? Date.now;
  const nowMs = nowFn();

  try {
    const existing = state.get(codeName);
    if (existing && !hasElapsed(existing.lastCheckedAt, nowMs, MIN_CHECK_INTERVAL_MS)) return;

    if (entitlementLatch(existing, nowMs)) {
      // Quiet by design (AC5). An un-entitled org is a normal, expected steady
      // state — logging it every half hour on every agent would train operators
      // to ignore the log rather than tell them anything.
      state.set(codeName, { lastCheckedAt: nowMs, notEntitledAt: existing?.notEntitledAt ?? null });
      return;
    }

    // Stamp BEFORE the scan, not after. An await here yields to the tick, and
    // a stamp on the far side would let a slow scan be re-entered by the next
    // poll — two concurrent scans sharing one cursor file, where the second
    // writer's save silently discards the first's progress.
    state.set(codeName, { lastCheckedAt: nowMs, notEntitledAt: existing?.notEntitledAt ?? null });

    // ENG-8726: the charter decides whether ANY target may be disclosed. This is
    // the producer for a flag that had none — see agent-logging-mode.
    //
    // The unknown case is logged for the same reason the missing salt is: it
    // withholds silently, and an all-`none` table is indistinguishable from a
    // correctly-enforced hash-only agent. One line per 10 minutes is the price
    // of being able to tell those apart afterwards.
    const loggingMode = readAgentLoggingMode(codeName, args.homeDir);
    const hashOnly = loggingModeWithholdsTargets(loggingMode);
    if (loggingMode.mode === null) {
      log(
        `[tool-call-audit] ${codeName}: charter logging_mode not established ` +
          `(${loggingMode.reason}) — withholding ALL targets for this agent`,
      );
    }

    // Still resolved for a hash-only agent, so the salt is stable if the charter
    // later flips to a disclosing mode — a salt generated late is a salt whose
    // hashes do not join the earlier ones.
    const salt = readToolCallPathSalt(codeName, args.homeDir);
    if (!salt && !hashOnly) {
      // Not fatal, but not silent either: without a salt every file target
      // degrades to kind 'none', so the table would look populated while being
      // blind to Read/Write/Edit. That is worth one line per 10 minutes.
      //
      // Suppressed when the charter withholds everything anyway: there the line
      // is true but misleading, sending an operator after a salt problem that
      // changes nothing. The logging_mode line above is the one that explains
      // that table.
      log(`[tool-call-audit] ${codeName}: no path-hash salt available — file targets withheld`);
    }

    const home = args.homeDir ?? (process.env['HOME']?.trim() || homedir());
    const projectsRoot = args.projectsRoot ?? join(home, '.claude', 'projects');
    const transcriptDir = args.transcriptDir ?? sessionTranscriptDir(getProjectDir(codeName));

    const current = peekCurrentSession(codeName);
    const sessionIds = current ? [current.sessionId] : [];

    const summary = await scanAgentToolCalls({
      api,
      agentId,
      codeName,
      transcriptDir,
      projectsRoot,
      sessionIds,
      pathHashSalt: salt ?? '',
      hashOnly,
      log,
      now: nowFn,
      ...(args.homeDir === undefined ? {} : { homeDir: args.homeDir }),
      ...(args.cursorPath === undefined ? {} : { cursorPath: args.cursorPath }),
    });

    // Set the latch only on the scanner's explicit `notEntitled`, which comes
    // from the route's own `entitled: false`. Nothing else may set it: a
    // transport fault, an oversize batch and an unbound agent are all held by
    // the cursor contract and must be retried, not latched away.
    if (summary.notEntitled) {
      state.set(codeName, { lastCheckedAt: nowMs, notEntitledAt: nowMs });
    } else if (summary.callsIngested > 0) {
      // Observed the org actually being collected, so drop any stale latch.
      // Gated on rows genuinely landing, not merely on the absence of a
      // refusal: a scan with nothing to send learned nothing about
      // entitlement, and clearing on that would turn the recheck window into
      // "every 10 minutes" for exactly the un-entitled orgs it exists to
      // spare.
      state.set(codeName, { lastCheckedAt: nowMs, notEntitledAt: null });
    }
  } catch (err) {
    // The manager call site is a bare `void` with no `.catch()`, matching its
    // siblings, so containment has to happen here or an unhandled rejection
    // escapes into the poll cycle.
    log(`[tool-call-audit] ${codeName}: scan failed: ${(err as Error).message}`);
  }
}
